/**
 * AchievementBadge — displays a single badge with locked/unlocked state.
 * Iron and steel aesthetic. Not cartoonish.
 */

import { View, Text, StyleSheet } from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import type { AchievementState } from '@/lib/achievements';
import {
  Dumbbell,
  Target,
  Swords,
  Flame,
  TrendingUp,
  Gem,
  Share2,
  Shield,
  Lock,
} from 'lucide-react-native';
import type { LucideIcon } from 'lucide-react-native';

const ICON_MAP: Record<string, LucideIcon> = {
  Dumbbell,
  Target,
  Swords,
  Flame,
  TrendingUp,
  Gem,
  Share2,
  Shield,
};

const TIER_COLORS: Record<string, string> = {
  bronze: '#8B6914',
  silver: '#A0A0A0',
  gold: '#C9A825',
  diamond: '#7DF9FF',
};

interface AchievementBadgeProps {
  achievement: AchievementState;
  size?: 'small' | 'normal';
}

export default function AchievementBadge({ achievement, size = 'normal' }: AchievementBadgeProps) {
  const { theme } = useTheme();
  const c = theme.colors;
  const isSmall = size === 'small';
  const iconSize = isSmall ? 18 : 24;
  const tierColor = TIER_COLORS[achievement.tier] ?? c.textTertiary;

  const IconComponent = ICON_MAP[achievement.icon] ?? Shield;

  return (
    <View style={[
      styles.container,
      isSmall && styles.containerSmall,
      {
        backgroundColor: achievement.unlocked ? c.surface : c.surfaceVariant,
        borderColor: achievement.unlocked ? tierColor + '80' : c.border,
        opacity: achievement.unlocked ? 1 : 0.5,
      },
    ]}>
      <View style={[
        styles.iconWrap,
        isSmall && styles.iconWrapSmall,
        { backgroundColor: achievement.unlocked ? tierColor + '20' : 'transparent' },
      ]}>
        {achievement.unlocked ? (
          <IconComponent size={iconSize} color={tierColor} />
        ) : (
          <Lock size={iconSize} color={c.textTertiary} />
        )}
      </View>
      <Text
        style={[
          styles.name,
          isSmall && styles.nameSmall,
          { color: achievement.unlocked ? c.text : c.textTertiary },
        ]}
        numberOfLines={1}
      >
        {achievement.name}
      </Text>
      {!isSmall && (
        <Text style={[styles.desc, { color: c.textTertiary }]} numberOfLines={2}>
          {achievement.description}
        </Text>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: 100,
    alignItems: 'center',
    padding: 10,
    borderRadius: 2,
    borderWidth: 1,
    gap: 6,
  },
  containerSmall: {
    width: 72,
    padding: 6,
    borderRadius: 2,
    gap: 4,
  },
  iconWrap: {
    width: 44,
    height: 44,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconWrapSmall: {
    width: 32,
    height: 32,
    borderRadius: 4,
  },
  name: {
    fontSize: 11,
    fontWeight: '700',
    textAlign: 'center',
    letterSpacing: 0.3,
  },
  nameSmall: {
    fontSize: 11,
  },
  desc: {
    fontSize: 11,
    textAlign: 'center',
    lineHeight: 12,
  },
});
